import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCollection } from '@/composable/useCollection.js'
import { useUserStore } from './userStore.js'
import { useCharacterStore } from './characterStore.js'
import { db } from '@/firebase/config.js'
import { collection, setDoc, doc, addDoc, updateDoc, onSnapshot } from 'firebase/firestore'
import { usePerformanceStore } from './performanceStore.js'

interface Skill {
  skill: string
  rank: number
  source: string
  id: string
  subSkills: string[]
}

export interface ManualSkill {
  skill: string
  description: string
  attribute: string
  subSkills: string[]
}

export const useSkillStore = defineStore('skill', {
  state: () => ({
    skills: {},
    allSkills: [],
    effectiveSkills: [
      {
        skill: 'nothing',
        rank: 2,
        description: 'something',
        isOrigin: false,
        id: 'huh',
        attribute: 'Agility',
        source: 'Test',
        subSkills: ['s']
      }
    ]
  }),
  getters: {
    getSkills: (state) => state.skills,
    getAllSkills: (state): Array<ManualSkill> => state.allSkills,
    getEffectiveSkills: (state) => state.effectiveSkills
  },
  actions: {
    async setSkill(skill: any) {
      skill.subSkills = this.effectiveSkills[skill.index].subSkills
      if (skill.subSkills?.length >= 1 && skill.rank < 3) {
        skill.subSkills = []
      }

      if (skill.subSkills?.length >= 2 && skill.rank < 5) {
        skill.subSkills = skill.subSkills.slice(0, 1)
      }
      if (skill.subSkills?.length >= 3 && skill.rank < 7) {
        skill.subSkills = skill.subSkills.slice(0, 2)
      }
      if (skill.subSkills?.length >= 4 && skill.rank < 9) {
        skill.subSkills = skill.subSkills.slice(0, 3)
      }
      if (skill.skill === 'Performance' && skill.rank < 10) {
        const performanceStore = usePerformanceStore()
        if (skill.rank < 5) {
          performanceStore.setPerformanceStyles({
            ...performanceStore.performanceStyles,
            style2: '',
            style3: ''
          })
          skill.subSkills = skill.subSkills.slice(0, 1)
        } else {
          performanceStore.setPerformanceStyles({
            ...performanceStore.performanceStyles,
            style3: ''
          })
        }
      }
      if (skill.rank == 0) {
        delete this.skills[skill.skill]
      } else {
        this.skills[skill.skill] = skill
      }

      this.setEffectiveSubSkills(skill.subSkills, skill.index)
      this.updateSkills(skill)
    },
    updateSkills(skillChanged) {
      updateDoc(
        doc(
          db,
          'User/' + useUserStore().getUserId + '/Character/' + useCharacterStore().getCharacterId
        ),
        { skills: this.skills, skillChanged: skillChanged }
      )
    },
    clearEffectiveSkills() {
      this.effectiveSkills = []
    },
    setEffectiveSkills() {
      const effectiveSkillsArr = Object.values(this.allSkills).sort((a: any, b: any) => {
        let aCode = a.skill.charCodeAt(0)
        let bCode = b.skill.charCodeAt(0)
        if (aCode - bCode == 0) {
          aCode = a.skill.charCodeAt(1)
          bCode = b.skill.charCodeAt(1)
          if (aCode - bCode == 0) {
            aCode = a.skill.charCodeAt(2)
            bCode = b.skill.charCodeAt(2)
          }
        }
        return aCode - bCode
      })
      const ret: Array<any> = []
      let index = 0
      effectiveSkillsArr.forEach((skill: ManualSkill) => {
        const obj = {
          id: this.skills[skill.skill]?.id || '',
          skill: skill.skill,
          rank: this.skills[skill.skill]?.rank || 0,
          source: this.skills[skill.skill]?.source || 'Base',
          subSkills: this.skills[skill.skill]?.subSkills || ([] as string[]),
          description: skill.description,
          attribute: skill.attribute,
          isOrigin: useCharacterStore().getOriginSkills.includes(skill.skill),
          index: index
        }
        ret.push(obj)
        index += 1
      })
      this.effectiveSkills = ret
    },
    updateSubSkills(skill: string, subSkills: string[]) {
      this.skills[skill].subSkills = subSkills
      this.setEffectiveSubSkills(subSkills, this.skills[skill].index)
      this.updateSkills(this.skills[skill])
    },
    setUpBuildDisplay(skillChanged) {
      if (this.effectiveSkills.length > 1) {
        if (skillChanged != undefined && skillChanged.skill) {
          this.effectiveSkills[skillChanged.index].rank = skillChanged.rank
          this.effectiveSkills[skillChanged.index].subSkills = skillChanged.subSkills
        }
      } else {
        this.setEffectiveSkills()
      }
    },
    setEffectiveOriginSkill(isOrigin: boolean, index: number) {
      this.effectiveSkills[index].isOrigin = isOrigin
    },
    setEffectiveSubSkills(subSkills: string[], index: number) {
      this.effectiveSkills[index].subSkills = subSkills
    },
    async setAllSkillsFromFirebase(skills: any) {
      this.allSkills = Object.values(skills)
    },
    setCharacterSkillsFromFirebase(skills: any) {
      this.skills = skills
    }
  },
  persist: true
})
